import type { CrmMessages } from "./englishCrmMessages";

/**
 * Catálogo del CRM en español mexicano.
 * Vocabulario: deals = prospectos, companies = empresas, contacts = contactos,
 * sales = equipo (usuarios del equipo), tasks = tareas, notes = notas,
 * stages = etapas.
 */
export const spanishCrmMessages = {
  resources: {
    companies: {
      name: "Empresa |||| Empresas",
      forcedCaseName: "Empresa",
      fields: {
        name: "Nombre de la empresa",
        website: "Sitio web",
        linkedin_url: "URL de LinkedIn",
        phone_number: "Teléfono",
        created_at: "Fecha de alta",
        nb_contacts: "Número de contactos",
        revenue: "Facturación",
        sector: "Giro",
        size: "Tamaño",
        tax_identifier: "RFC",
        address: "Dirección",
        city: "Ciudad",
        zipcode: "Código postal",
        state_abbr: "Estado",
        country: "País",
        description: "Descripción",
        context_links: "Ligas de contexto",
        sales_id: "Responsable de la cuenta",
      },
      empty: {
        description: "Parece que todavía no tienes empresas.",
        title: "No se encontraron empresas",
      },
      import: {
        title: "Importar empresas",
      },
      field_categories: {
        contact: "Contacto",
        additional_info: "Información adicional",
        address: "Dirección",
        context: "Contexto",
      },
      action: {
        create: "Crear empresa",
        edit: "Editar empresa",
        new: "Nueva empresa",
        show: "Ver empresa",
      },
      added_on: "Agregada el %{date}",
      followed_by: "A cargo de %{name}",
      followed_by_you: "A tu cargo",
      no_contacts: "Sin contactos",
      nb_contacts: "%{smart_count} contacto |||| %{smart_count} contactos",
      nb_deals: "%{smart_count} prospecto |||| %{smart_count} prospectos",
      sizes: {
        one_employee: "1 empleado",
        two_to_nine_employees: "2 a 9 empleados",
        ten_to_forty_nine_employees: "10 a 49 empleados",
        fifty_to_two_hundred_forty_nine_employees: "50 a 249 empleados",
        two_hundred_fifty_or_more_employees: "250 empleados o más",
      },
      autocomplete: {
        create_error: "Ocurrió un error al crear la empresa",
        create_item: "Crear %{item}",
        create_label: "Escribe para crear una empresa nueva",
      },
    },
    contacts: {
      name: "Contacto |||| Contactos",
      forcedCaseName: "Contacto",
      field_categories: {
        background_info: "Antecedentes",
        identity: "Identidad",
        misc: "Otros",
        personal_info: "Datos de contacto",
        position: "Puesto",
      },
      fields: {
        first_name: "Nombre",
        last_name: "Apellidos",
        last_seen: "Última actividad",
        title: "Puesto",
        company_id: "Empresa",
        email_jsonb: "Correos",
        email: "Correo",
        phone_jsonb: "Teléfonos",
        phone_number: "Teléfono",
        linkedin_url: "URL de LinkedIn",
        background: "Antecedentes (quién es, cómo lo conocieron, etc.)",
        has_newsletter: "Recibe boletín",
        sales_id: "Responsable de la cuenta",
      },
      action: {
        add: "Agregar contacto",
        add_first: "Agrega tu primer contacto",
        create: "Crear contacto",
        edit: "Editar contacto",
        export_vcard: "Exportar a vCard",
        new: "Nuevo contacto",
        show: "Ver contacto",
      },
      background: {
        last_activity_on: "Última actividad el %{date}",
        added_on: "Agregado el %{date}",
        followed_by: "A cargo de %{name}",
        followed_by_you: "A tu cargo",
        status_none: "Ninguno",
      },
      position_at: "%{title} en",
      position_at_company: "%{title} en %{company}",
      empty: {
        description: "Parece que todavía no tienes contactos.",
        title: "No se encontraron contactos",
      },
      import: {
        title: "Importar contactos",
      },
      inputs: {
        genders: {
          male: "Él",
          female: "Ella",
          nonbinary: "Elle",
        },
        personal_info_types: {
          work: "Trabajo",
          home: "Casa",
          other: "Otro",
        },
      },
      list: {
        error_loading: "Error al cargar los contactos",
      },
      bulk_tag: {
        action: "Etiquetar",
        back: "Volver a etiquetas",
        create_description:
          "Crea una etiqueta nueva y aplícala a los contactos seleccionados.",
        description:
          "Elige una etiqueta existente o crea una nueva para los contactos seleccionados.",
        empty:
          "Todavía no hay etiquetas. Crea una para etiquetar los contactos seleccionados.",
        error: "No se pudo agregar la etiqueta a los contactos",
        noop: "Los contactos seleccionados ya tienen esta etiqueta",
        success:
          "Etiqueta agregada a %{smart_count} contacto |||| Etiqueta agregada a %{smart_count} contactos",
        title: "Etiquetar contactos",
      },
      merge: {
        action: "Fusionar con otro contacto",
        confirm: "Fusionar contactos",
        current_contact: "Contacto actual (se eliminará)",
        description: "Fusiona este contacto con otro.",
        error: "No se pudieron fusionar los contactos",
        merging: "Fusionando...",
        no_additional_data: "No hay datos adicionales que fusionar",
        select_target: "Elige el contacto con el que quieres fusionarlo",
        success: "Contactos fusionados",
        target_contact: "Contacto destino (se conserva)",
        title: "Fusionar contacto",
        warning_description:
          "Todos los datos pasarán al segundo contacto. Esta acción no se puede deshacer.",
        warning_title: "Cuidado: esta acción es irreversible",
        what_will_be_merged: "Qué se va a fusionar:",
        notes_reassigned:
          "%{smart_count} nota se reasignará |||| %{smart_count} notas se reasignarán",
        tasks_reassigned:
          "%{smart_count} tarea se reasignará |||| %{smart_count} tareas se reasignarán",
        deals_updated:
          "%{smart_count} prospecto se actualizará |||| %{smart_count} prospectos se actualizarán",
        emails_added:
          "%{smart_count} correo se agregará |||| %{smart_count} correos se agregarán",
        phones_added:
          "%{smart_count} teléfono se agregará |||| %{smart_count} teléfonos se agregarán",
      },
      filters: {
        before_last_month: "Antes del mes pasado",
        before_this_month: "Antes de este mes",
        before_this_week: "Antes de esta semana",
        managed_by_me: "A mi cargo",
        search: "Buscar nombre, empresa...",
        this_week: "Esta semana",
        today: "Hoy",
        tags: "Etiquetas",
        tasks: "Tareas",
      },
      hot: {
        empty_change_status:
          'Cambia el estatus de un contacto agregándole una nota y haciendo clic en "mostrar opciones".',
        empty_hint: 'Aquí aparecerán los contactos con estatus "caliente".',
        title: "Contactos calientes",
      },
    },
    deals: {
      name: "Prospecto |||| Prospectos",
      fields: {
        name: "Nombre",
        description: "Descripción",
        company_id: "Empresa",
        contact_ids: "Contactos",
        category: "Categoría",
        amount: "Presupuesto",
        expected_closing_date: "Fecha estimada de cierre",
        stage: "Etapa",
      },
      action: {
        back_to_deal: "Volver al prospecto",
        create: "Crear prospecto",
        new: "Nuevo prospecto",
      },
      field_categories: {
        misc: "Otros",
      },
      filters: {
        only_mine: "Solo los prospectos a mi cargo",
      },
      archived: {
        action: "Archivar",
        error: "Error: no se archivó el prospecto",
        list_title: "Prospectos archivados",
        success: "Prospecto archivado",
        title: "Prospecto archivado",
        view: "Ver prospectos archivados",
      },
      inputs: {
        linked_to: "Vinculado a",
      },
      unarchived: {
        action: "Regresar al tablero",
        error: "Error: no se desarchivó el prospecto",
        success: "Prospecto desarchivado",
      },
      updated: "Prospecto actualizado",
      empty: {
        before_create: "antes de crear un prospecto.",
        description: "Parece que todavía no tienes prospectos.",
        title: "No se encontraron prospectos",
      },
      import: {
        title: "Importar prospectos",
      },
      invalid_date: "Fecha no válida",
    },
    notes: {
      name: "Nota |||| Notas",
      forcedCaseName: "Nota",
      fields: {
        status: "Estatus",
        date: "Fecha",
        attachments: "Archivos adjuntos",
        contact_id: "Contacto",
        deal_id: "Prospecto",
      },
      action: {
        add: "Agregar nota",
        add_first: "Agrega tu primera nota",
        delete: "Eliminar nota",
        edit: "Editar nota",
        update: "Actualizar nota",
        add_this: "Agregar esta nota",
      },
      sheet: {
        create: "Crear nota",
        create_for: "Crear nota para %{name}",
        edit: "Editar nota",
        edit_for: "Editar nota de %{name}",
      },
      deleted: "Nota eliminada",
      empty: "Todavía no hay notas",
      author_added: "%{name} agregó una nota",
      you_added: "Agregaste una nota",
      me: "Yo",
      list: {
        error_loading: "Error al cargar las notas",
      },
      note_for_contact: "Nota para %{name}",
      stepper: {
        hint: "Entra a la página de un contacto y agrega una nota",
      },
      added: "Nota agregada",
      inputs: {
        add_note: "Agrega una nota",
        options_hint: "(adjuntar archivos o cambiar detalles)",
        show_options: "Mostrar opciones",
      },
      actions: {
        attach_document: "Adjuntar documento",
      },
      validation: {
        note_or_attachment_required: "Escribe una nota o adjunta un archivo",
      },
    },
    sales: {
      name: "Usuario del equipo |||| Equipo",
      fields: {
        first_name: "Nombre",
        last_name: "Apellidos",
        email: "Correo",
        secondary_email: "Correo secundario",
        secondary_emails: "Correos secundarios",
        administrator: "Administrador",
        disabled: "Desactivado",
      },
      create: {
        error: "Ocurrió un error al crear el usuario.",
        success:
          "Usuario creado. En breve recibirá un correo para crear su contraseña.",
        title: "Crear un usuario nuevo",
      },
      edit: {
        error: "Ocurrió un error. Inténtalo de nuevo.",
        record_not_found: "No se encontró el registro",
        success: "Usuario actualizado",
        title: "Editar a %{name}",
      },
      action: {
        new: "Nuevo usuario",
      },
    },
    tasks: {
      name: "Tarea |||| Tareas",
      forcedCaseName: "Tarea",
      fields: {
        text: "Descripción",
        due_date: "Fecha límite",
        type: "Tipo",
        contact_id: "Contacto",
        due_short: "vence",
      },
      action: {
        add: "Agregar tarea",
        create: "Crear tarea",
        edit: "Editar tarea",
      },
      actions: {
        postpone_next_week: "Posponer a la próxima semana",
        postpone_tomorrow: "Posponer a mañana",
        title: "acciones de la tarea",
      },
      added: "Tarea agregada",
      deleted: "Tarea eliminada",
      dialog: {
        create: "Crear tarea",
        create_for: "Crear tarea para %{name}",
      },
      sheet: {
        edit: "Editar tarea",
        edit_for: "Editar tarea de %{name}",
      },
      empty: "Todavía no hay tareas",
      empty_list_hint:
        "Aquí aparecerán las tareas que agregues a tus contactos.",
      filters: {
        later: "Después",
        overdue: "Vencidas",
        this_week: "Esta semana",
        today: "Hoy",
        tomorrow: "Mañana",
        with_pending: "Con tareas pendientes",
      },
      regarding_contact: "(Sobre: %{name})",
      updated: "Tarea actualizada",
    },
    tags: {
      name: "Etiqueta |||| Etiquetas",
      action: {
        add: "Agregar etiqueta",
        create: "Crear etiqueta nueva",
      },
      dialog: {
        color: "Color",
        create_title: "Crear una etiqueta nueva",
        edit_title: "Editar etiqueta",
        name_label: "Nombre de la etiqueta",
        name_placeholder: "Escribe el nombre de la etiqueta",
      },
    },
  },
  crm: {
    action: {
      reset_password: "Restablecer contraseña",
    },
    auth: {
      first_name: "Nombre",
      last_name: "Apellidos",
      confirm_password: "Confirmar contraseña",
      confirmation_required:
        "Abre la liga que te acabamos de mandar por correo para confirmar tu cuenta.",
      recovery_email_sent:
        "Si tienes cuenta, en unos momentos te llegará un correo para recuperar tu contraseña.",
      sign_in_failed: "No se pudo iniciar sesión.",
      sign_in_google_workspace: "Iniciar sesión con Google Workspace",
      signup: {
        create_account: "Crear cuenta",
        create_first_user:
          "Crea la primera cuenta de usuario para terminar la configuración.",
        creating: "Creando...",
        initial_user_created: "Usuario inicial creado",
      },
      welcome_title: "Bienvenido",
    },
    common: {
      account_manager: "Responsable de la cuenta",
      activity: "Actividad",
      added: "agregó",
      details: "Detalles",
      last_activity_with_date: "última actividad %{date}",
      load_more: "Cargar más",
      misc: "Otros",
      past: "Pasadas",
      read_more: "Leer más",
      retry: "Reintentar",
      back: "Regresar",
      back_to_list: "Regresar a la lista",
      show_less: "Ver menos",
      copied: "¡Copiado!",
      copy: "Copiar",
      loading: "Cargando...",
      me: "Yo",
      task_count: "%{smart_count} tarea |||| %{smart_count} tareas",
    },
    changelog: {
      title: "Historial de cambios",
    },
    activity: {
      added_company: "%{name} agregó la empresa",
      you_added_company: "Agregaste la empresa",
      added_contact: "%{name} agregó a",
      you_added_contact: "Agregaste a",
      added_note: "%{name} agregó una nota sobre",
      you_added_note: "Agregaste una nota sobre",
      added_note_about_deal: "%{name} agregó una nota sobre el prospecto",
      you_added_note_about_deal: "Agregaste una nota sobre el prospecto",
      added_deal: "%{name} agregó el prospecto",
      you_added_deal: "Agregaste el prospecto",
      at_company: "en",
      to: "a",
      load_more: "Cargar más actividad",
    },
    dashboard: {
      deals_chart: "Ingresos esperados de prospectos",
      deals_pipeline: "Embudo de prospectos",
      latest_activity: "Actividad reciente",
      latest_activity_error: "Error al cargar la actividad reciente",
      latest_notes: "Mis notas recientes",
      latest_notes_added_ago: "agregada %{timeAgo}",
      stepper: {
        install: "Instalar la plataforma",
        progress: "%{step}/3 listos",
        whats_next: "¿Qué sigue?",
      },
      upcoming_tasks: "Próximas tareas",
    },
    data_import: {
      button: "Importar CSV",
      complete:
        "Importación terminada. Se importaron %{importCount} registros, con %{errorCount} errores",
      csv_file: "Archivo CSV",
      error:
        "No se pudo importar este archivo; asegúrate de que sea un CSV válido.",
      in_progress: "Importando…",
      progress:
        "Importados %{importCount} de %{rowCount} registros, con %{errorCount} errores.",
      remaining_time: "Tiempo restante estimado:",
      resource: "Recurso",
      sample_download: "Descargar CSV de ejemplo",
      sample_hint:
        "Aquí tienes un CSV de ejemplo que puedes usar como plantilla",
      start: "Iniciar importación",
      stop: "Detener importación",
      stopped:
        "Importación detenida. Se importaron %{importCount} registros, con %{errorCount} errores",
      title: "Importar datos",
    },
    header: {
      import_data: "Importar desde JSON",
    },
    image_editor: {
      change: "Cambiar",
      drop_hint:
        "Suelta aquí un archivo para subirlo, o haz clic para elegirlo.",
      editable_content: "Contenido editable",
      title: "Subir y recortar imagen",
      update_image: "Actualizar imagen",
    },
    import: {
      action: {
        download_error_report: "Descargar el reporte de errores",
        import: "Importar",
        import_another: "Importar otro archivo",
      },
      error: {
        unable: "No se pudo importar este archivo.",
      },
      idle: {
        description_1:
          "Puedes importar usuarios del equipo, empresas, contactos, notas y tareas.",
        description_2:
          "Los datos deben estar en un archivo JSON como el siguiente ejemplo:",
      },
      status: {
        all_success: "Todos los registros se importaron correctamente.",
        complete: "Importación terminada.",
        failed: "Fallidos",
        imported: "Importados",
        in_progress: "Importando; no salgas de esta página.",
        some_failed: "Algunos registros no se importaron.",
        table_caption: "Estado de la importación",
      },
      title: "Importar desde JSON",
    },
    settings: {
      about: "Acerca de",
      companies: {
        sectors: "Giros",
      },
      dark_mode_logo: "Logo para modo oscuro",
      deals: {
        categories: "Categorías",
        currency: "Moneda",
        pipeline_help:
          "Elige qué etapas de prospecto cuentan como parte del embudo.",
        pipeline_statuses: "Etapas del embudo",
        stages: "Etapas",
      },
      light_mode_logo: "Logo para modo claro",
      notes: {
        statuses: "Estatus",
      },
      reset_defaults: "Restablecer valores predeterminados",
      save_error: "No se pudo guardar la configuración",
      saved: "Configuración guardada",
      saving: "Guardando...",
      tasks: {
        types: "Tipos",
      },
      preferences: "Preferencias",
      title: "Configuración",
      app_title: "Nombre de la aplicación",
      sections: {
        branding: "Marca",
      },
      validation: {
        duplicate: "%{display_name} repetidas: %{items}",
        in_use:
          "No se pueden quitar %{display_name} que todavía usan algunos prospectos: %{items}",
        validating: "Validando…",
        entities: {
          categories: "categorías",
          stages: "etapas",
        },
      },
    },
    theme: {
      dark: "Oscuro",
      label: "Tema",
      light: "Claro",
      system: "Sistema",
    },
    language: "Idioma",
    navigation: {
      label: "Navegación",
    },
    profile: {
      add_secondary_email: "Agregar un correo",
      email_taken: "%{email} ya lo usa otro usuario",
      no_secondary_emails: "Ninguno",
      secondary_email_invalid: "%{email} no es un correo válido",
      secondary_email_is_primary: "%{email} ya es tu correo principal",
      secondary_email_taken: "%{email} ya lo usa otro usuario",
      too_many_secondary_emails:
        "No puedes agregar más de 10 correos secundarios",
      secondary_emails_help:
        "Otros correos desde los que mandas mensajes. Deja uno vacío para quitarlo.",
      inbound: {
        description:
          "Puedes empezar a mandar correos a la dirección de entrada de tu servidor, por ejemplo agregándola en el campo %{field}. La plataforma procesará los correos y agregará notas a los contactos correspondientes.",
        title: "Correo de entrada",
      },
      mcp: {
        title: "Servidor MCP",
        description:
          "Usa esta URL para conectar tu asistente de IA a los datos del CRM mediante el Model Context Protocol (MCP).",
      },
      password: {
        change: "Cambiar contraseña",
      },
      password_reset_sent:
        "Te enviamos un correo para restablecer tu contraseña",
      record_not_found: "No se encontró el registro",
      title: "Perfil",
      updated: "Tu perfil se actualizó",
      update_error: "Ocurrió un error. Inténtalo de nuevo",
    },
    validation: {
      invalid_url: "Debe ser una URL válida",
      invalid_linkedin_url: "La URL debe ser de linkedin.com",
    },
  },
} satisfies CrmMessages;
